'use client'

import React from 'react'
import { AlertCircle, CheckCircle2, MessageSquareReply } from 'lucide-react'
import type { ProcessedTheme } from '../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../../ProcessedEntry/DomainEntry/processedTypes'
import type { BonResultMessage, VerdictBoxData, VerdictTone } from './ports'

// Backoffice-styled renderers for the public surfaces' result shapes. The
// content (which lines, in which order, which tone) comes from ports.ts; this
// file only decides how a bubble, a verdict box or a result list looks in the
// backoffice's light/dark themes. Not pixel replicas of the apps.

type LineTone = 'danger' | 'safe' | 'warning' | 'neutral'

const toneOf = (status: BonResultMessage['status']): LineTone => (status === 'error' ? 'danger' : status === 'success' ? 'safe' : 'neutral')

// ─── Burla ou Não — result bubbles (Card1Chat ResultView) ─────────────────────

export function ChatBubbles({ messages, theme }: { messages: BonResultMessage[]; theme: ProcessedTheme }) {
  const dark = theme === 'dark'
  return (
    <div className="flex flex-col gap-2 py-2" data-preview-body="bon-chat">
      {messages.map((msg, i) => {
        const tone = toneOf(msg.status)
        const bubble =
          tone === 'danger'
            ? dark ? 'border-destructive/40 bg-danger-surface text-destructive' : 'border-destructive/40 bg-danger-surface text-destructive'
            : tone === 'safe'
              ? dark ? 'border-teal-900 bg-teal-950/50 text-teal-100' : 'border-teal-200 bg-teal-50 text-teal-900'
              : dark ? 'border-border bg-secondary text-foreground' : 'border-border bg-card text-foreground'
        const Icon = tone === 'danger' ? AlertCircle : MessageSquareReply
        return (
          <div key={i} className={`flex items-start gap-2 rounded-2xl border px-3 py-2 text-[12px] leading-5 ${bubble}`} data-preview-line data-tone={tone}>
            <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tone === 'danger' ? 'text-destructive' : 'text-teal-500'}`} />
            <span className="whitespace-pre-wrap break-words">{msg.text}</span>
          </div>
        )
      })}
    </div>
  )
}

// ─── É Legítimo / domain checker — verdict box ────────────────────────────────

const BOX_TONE: Record<VerdictTone, { light: string; dark: string; line: LineTone }> = {
  danger:  { light: 'border-destructive/40 bg-danger-surface text-destructive',         dark: 'border-destructive/40 bg-danger-surface text-destructive',         line: 'danger' },
  error:   { light: 'border-destructive/40 bg-danger-surface text-destructive',         dark: 'border-destructive/40 bg-danger-surface text-destructive',         line: 'danger' },
  warning: { light: 'border-warning-border bg-warning-surface text-warning',   dark: 'border-warning-border bg-warning-surface text-warning',   line: 'warning' },
  safe:    { light: 'border-success/40 bg-success-surface text-success', dark: 'border-success/40 bg-success-surface text-success', line: 'safe' },
}

export function VerdictBox({ verdict, theme, variant }: { verdict: VerdictBoxData; theme: ProcessedTheme; variant: 'elegitimo' | 'domain-checker' }) {
  const t = BOX_TONE[verdict.tone]
  const cls = theme === 'dark' ? t.dark : t.light
  // É Legítimo draws a soft 1.5rem card; the domain checker a tighter box.
  const radius = variant === 'elegitimo' ? 'rounded-[1.5rem]' : ''
  return (
    <div className={`my-2 overflow-hidden border p-4 ${radius} ${cls}`} data-preview-body={variant} data-tone={t.line}>
      <p className="text-[13px] font-bold" data-preview-line data-tone={t.line}>{verdict.label}</p>
      <p className="mt-1 text-[12px] leading-5" data-preview-line data-tone={t.line}>{verdict.message}</p>
      {verdict.items.length > 0 && (
        <ul className="mt-3 space-y-2 text-[12px]">
          {verdict.items.map((item) => (
            <li key={item} className="flex gap-2 leading-5" data-preview-line data-tone={t.line}>
              <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Validators — "Resultado" list + error box ────────────────────────────────

export function ResultList({ messages, error, theme, waiting }: { messages: string[]; error: string | null; theme: ProcessedTheme; waiting?: boolean }) {
  const c = watchClasses(theme)
  const dark = theme === 'dark'
  return (
    <div className="flex flex-col gap-2 py-2" data-preview-body="validator">
      {messages.length > 0 && (
        <div className={`border p-4 ${dark ? 'border-signal/30 bg-signal-surface' : 'border-signal/30 bg-signal-surface'}`}>
          <div className={`mb-2 flex items-center gap-2 text-[12px] font-semibold ${c.text}`}>
            <CheckCircle2 className="h-4 w-4 text-signal" />
            Resultado
          </div>
          <ul className={`space-y-1 text-[12px] leading-5 ${c.muted}`}>
            {messages.map((message) => (
              <li key={message} data-preview-line data-tone="neutral">{message}</li>
            ))}
          </ul>
        </div>
      )}
      {error && (
        <div
          className={`flex gap-2 border px-3 py-2 text-[12px] ${dark ? 'border-destructive/40 bg-danger-surface text-destructive' : 'border-destructive/40 bg-danger-surface text-destructive'}`}
          data-preview-line
          data-tone="danger"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {messages.length === 0 && !error && (
        <p className={`px-1 text-[11px] italic ${c.faint}`} data-preview-line data-tone="neutral">
          {waiting ? 'O validador não mostra nada enquanto espera pela resposta.' : 'O validador não teria nada para mostrar.'}
        </p>
      )}
    </div>
  )
}

// ─── Plain note (empty states) ────────────────────────────────────────────────

export function PreviewNote({ children, theme, tone = 'neutral' }: { children: React.ReactNode; theme: ProcessedTheme; tone?: LineTone }) {
  const c = watchClasses(theme)
  const cls =
    tone === 'warning'
      ? 'border-warning-border bg-warning-surface text-warning'
      : `${c.line} ${c.soft} ${c.muted}`
  return (
    <div className={`my-2 border px-3 py-2 text-[12px] leading-5 ${cls}`} data-preview-line data-tone={tone}>
      {children}
    </div>
  )
}
